import { useEffect, useRef, useState } from 'react'
import type { Dispatch, RefObject, SetStateAction } from 'react'

export interface AnchoredPopoverPosition {
  readonly top: number
  readonly right: number
}

export interface AnchoredPopover<
  TTrigger extends HTMLElement = HTMLButtonElement,
  TPanel extends HTMLElement = HTMLDivElement,
> {
  readonly triggerRef: RefObject<TTrigger | null>
  readonly panelRef: RefObject<TPanel | null>
  /** `undefined` until first measured after opening — callers should only
   * portal/render the panel once this is set (see usage in `CartLineRow`). */
  readonly position: AnchoredPopoverPosition | undefined
}

/**
 * Shared positioning + outside-click/Escape/scroll-close wiring for a
 * TRIGGER + PORTALED PANEL popover (a dropdown menu, a disclosure popover)
 * that needs to escape an ancestor's `overflow: hidden` (e.g. a rounded
 * card clipping its corners) — see `CartLineRow`'s `RowMenu` and
 * `VolumeDiscountDisclosure` for the concrete dropdown-clipping bug this
 * fixes (the cart line-table card's `overflow: hidden` was clipping both
 * the row's ⋮ menu and the Volume-pricing popover).
 *
 * Returns refs to attach to the trigger and the portaled panel, plus the
 * panel's computed `{top, right}` (`position: fixed`, anchored to the
 * trigger's own bottom-right corner via `getBoundingClientRect()` — chosen
 * so the caller doesn't need to know the panel's width up front).
 *
 * Takes a `useState` SETTER (not a bespoke close callback) specifically so
 * this hook's own effect dependency array stays stable across renders —
 * `setOpen` from `useState` is referentially stable by React's own
 * contract, unlike an inline `() => setOpen(false)` closure would be.
 *
 * Deliberately CLOSES (rather than repositions) on scroll/resize — a
 * short-lived dropdown doesn't need to track the trigger's moving position
 * mid-scroll, and closing is simpler/safer than staying open with a stale
 * position.
 */
export function useAnchoredPopover<
  TTrigger extends HTMLElement = HTMLButtonElement,
  TPanel extends HTMLElement = HTMLDivElement,
>(open: boolean, setOpen: Dispatch<SetStateAction<boolean>>): AnchoredPopover<TTrigger, TPanel> {
  const [position, setPosition] = useState<AnchoredPopoverPosition>()
  const triggerRef = useRef<TTrigger>(null)
  const panelRef = useRef<TPanel>(null)

  useEffect(() => {
    if (!open) return

    function updatePosition() {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      setPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
    }
    updatePosition()

    function handleOutsideMouseDown(event: MouseEvent) {
      const target = event.target as Node
      if (triggerRef.current?.contains(target)) return
      if (panelRef.current?.contains(target)) return
      setOpen(false)
    }
    // Code review W5 (2026-08-27) — Escape must dismiss THIS popover without
    // also closing an ancestor `Modal` (`ConfigurePartsModal`'s certificates
    // popover, opened while the Bulk-configure modal itself is open, was the
    // reported case: Escape closed the WHOLE modal instead of just the
    // popover). `Modal.tsx`'s own Escape handler is a document `keydown`
    // listener attached in the (default) BUBBLE phase as soon as the modal
    // opens — strictly earlier than this popover's listener, which only
    // attaches once the popover itself opens. Listener registration order
    // alone can't make this one win.
    //
    // The fix uses event PHASE instead of registration order: this listener
    // is registered with `capture: true`, so it runs during the CAPTURING
    // phase — which always completes before ANY bubble-phase listener on the
    // same `document` node fires, regardless of which was `addEventListener`'d
    // first. Calling `stopPropagation()` here during capture aborts the
    // event's entire remaining trip (capture AND bubble), so `Modal`'s
    // bubble-phase Escape handler never runs at all. First Escape closes only
    // this popover; a second, later Escape (no popover open, nothing left to
    // intercept it) reaches `Modal` and closes the modal — exactly the
    // nested-layer behavior AC7 expects.
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        setOpen(false)
      }
    }
    function handleClose() {
      setOpen(false)
    }

    document.addEventListener('mousedown', handleOutsideMouseDown)
    document.addEventListener('keydown', handleKeyDown, true)
    window.addEventListener('scroll', handleClose, true)
    window.addEventListener('resize', handleClose)
    return () => {
      document.removeEventListener('mousedown', handleOutsideMouseDown)
      document.removeEventListener('keydown', handleKeyDown, true)
      window.removeEventListener('scroll', handleClose, true)
      window.removeEventListener('resize', handleClose)
    }
  }, [open, setOpen])

  return { triggerRef, panelRef, position }
}
